// 카메라 쪽 컷어웨이(그레이박스·키트 공용). 수평 시선 방향으로 앞뒤·옆 거리를 구한다.
// 영웅보다 카메라 쪽에 있는 벽·문·소품은 윗부분을 CUT_H로 낮추고(콘셉트 r1-dungeon·c-mob-sweep처럼 앞쪽 벽이 낮게 잘린 구도),
// 여신상·큰 기둥·나무처럼 납작하면 어색한 개체는 화면 디더로 비쳐 보이게 한다.
// 블록 중심 기준이라 비스듬히 잘리지 않고, 영웅이 움직이면 1.3m 폭으로 부드럽게 오르내린다.
import * as THREE from 'three/webgpu';
import { abs, attribute, float, fract, min, mix, positionLocal, positionWorld, screenCoordinate, smoothstep, uniform, varying, vec2, vec3 } from 'three/tsl';

export const CUT_H = 0.6;
const cutHero = uniform(new THREE.Vector2(0, 0));
const cutOn = uniform(1);
/** 카메라에서 장면 쪽으로 향하는 수평 단위벡터. 기본은 -z */
const cutForward = uniform(new THREE.Vector2(0, -1));

export function setCutawayDirection(fx: number, fz: number): void {
  const len = Math.hypot(fx, fz);
  if (!Number.isFinite(len) || len < 1e-6) return;
  cutForward.value.set(fx / len, fz / len);
}
/** 컷 범위의 먼 끝(m, 영웅 기준 카메라 쪽). 카메라가 멀어지면(줌) 카메라와 영웅 사이 전체를 덮도록 늘린다 */
const cutFar = uniform(15);
let cutFarValue = 15;
/** 디더(나무·집처럼 납작하면 어색한 개체) 범위의 먼 끝(m). 기본 11, 줌으로 카메라가 멀어지면 늘린다 */
const ditherFar = uniform(11);

/** 보스 시선 디더: 보스(x, z)와 카메라 수평 위치(x, z). 보스전에서 보스~카메라 통로의 큰 기둥·바위를 비친다(z = 0이면 끔) */
const cutBoss = uniform(new THREE.Vector3(0, 0, 0));
const cutCam = uniform(new THREE.Vector2(0, 0));
let bossOnValue = 0;
const bossCpu = { x: 0, z: 0 }, camCpu = { x: 0, z: 0 };
/** 카메라 리그가 매 프레임 넘긴다: 보스 위치·보스전 정도(0..1)·카메라 수평 위치 */
export function setCutawayBoss(bx: number, bz: number, on: number, camX: number, camZ: number): void {
  bossOnValue = Number.isFinite(on) ? Math.min(1, Math.max(0, on)) : 0;
  cutBoss.value.set(bx, bz, bossOnValue);
  cutCam.value.set(camX, camZ);
  bossCpu.x = bx; bossCpu.z = bz; camCpu.x = camX; camCpu.z = camZ;
}

/** 셰이더 유니폼의 CPU 사본(QA 측정 도구·시험용 읽기 전용): 보스전 정도·컷/디더 범위·시선 방향·카메라 수평 위치 */
export function cutawayState(): { bossOn: number; cutFar: number; ditherFar: number; fx: number; fz: number; camX: number; camZ: number } {
  return { bossOn: bossOnValue, cutFar: cutFarValue, ditherFar: ditherFar.value as number, fx: cutForward.value.x, fz: cutForward.value.y, camX: camCpu.x, camZ: camCpu.z };
}

/** 보스 시선 디더 정도 0..1(셰이더와 같은 식, 시험·CPU 확인용) */
export function bossDitherFactor(cx: number, cz: number): number {
  const ux = camCpu.x - bossCpu.x, uz = camCpu.z - bossCpu.z;
  const len2 = ux * ux + uz * uz;
  if (len2 < 1e-4 || bossOnValue <= 0) return 0;
  const px = cx - bossCpu.x, pz = cz - bossCpu.z;
  const t = (px * ux + pz * uz) / len2;
  const perp = Math.abs(px * uz - pz * ux) / Math.sqrt(len2);
  return smooth(0.06, 0.2, t) * (1 - smooth(1.02, 1.12, t)) * (1 - smooth(bossLaneValue, bossLaneValue + 3, perp)) * bossOnValue;
}
/** 보스 시선 통로 기본 반폭(m): 보스 몸 폭(약 7m)의 반 + 기둥 반경. 큰 보스(비룡)는 setCutawayBossLane으로 넓힌다 */
export const BOSS_LANE_DEFAULT = 5.5;
let bossLaneValue = BOSS_LANE_DEFAULT;
const cutBossLane = uniform(BOSS_LANE_DEFAULT);
/** 보스 시선 통로 반폭(m)을 바꾼다(보스 그림이 프레임마다 넘긴다) */
export function setCutawayBossLane(w: number): void {
  bossLaneValue = Number.isFinite(w) && w > 0 ? w : BOSS_LANE_DEFAULT;
  cutBossLane.value = bossLaneValue;
}

/** 매 프레임 영웅(보간 위치)을 넘긴다. */
export function setCutawayFocus(x: number, z: number, enabled = true): void {
  cutHero.value.set(x, z);
  cutOn.value = enabled ? 1 : 0;
}

/** 카메라 리그가 매 프레임 넘긴다: 영웅에서 카메라까지 수평 거리(m)에 여유를 더한 값(최소 15m) */
export function setCutawayRange(far: number): void {
  cutFarValue = Math.max(15, far);
  cutFar.value = cutFarValue;
  ditherFar.value = Math.max(11, far);
}

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** 디더 방식(2) 키트의 비침 정도 0..1(셰이더 kd·kb·kc와 같은 식, 카메라 충돌·QA용 CPU 판정). 가림 칸(ko)은 넣지 않는다 */
export function kitDitherFactor(cx: number, cz: number): number {
  if (cutOn.value <= 0) return 0;
  const h = cutHero.value, f = cutForward.value;
  const dx = cx - h.x, dz = cz - h.y;
  const toward = -dx * f.x - dz * f.y;
  const side = Math.abs(-dx * f.y + dz * f.x);
  const df = ditherFar.value as number;
  const kd = smooth(-0.3, 1.0, toward) * (1 - smooth(df - 3, df, toward)) * (1 - smooth(2.6, 4.6, side));
  const kb = bossDitherFactor(cx, cz) * 1.2;
  const kc = (1 - smooth(9, 14, Math.hypot(cx - camCpu.x, cz - camCpu.z))) * bossOnValue;
  return Math.max(kd, kb, kc);
}

/** 컷 정도 0..1(셰이더와 같은 식). 문처럼 CPU에서 높이를 바꾸는 개체용 */
export function cutFactor(cx: number, cz: number, hx: number, hz: number): number {
  const dx = cx - hx, dz = cz - hz, f = cutForward.value;
  const toward = -dx * f.x - dz * f.y;
  const side = Math.abs(-dx * f.y + dz * f.x);
  return smooth(-0.3, 1.0, toward) * (1 - smooth(cutFarValue - 3, cutFarValue, toward)) * (1 - smooth(9, 12, side));
}

/**
 * 카메라 가림 물체 디더(2026-11-04 카메라 가림 레인): 카메라 리그(cameraCollider)가 카메라→영웅 시선을 실제로 가리는 키트 인스턴스를 찾아
 * 그 인스턴스의 aCut 중심(x, z)과 비침 정도(0..1)를 여기 칸에 넣는다. 셰이더는 중심이 같은(±5cm) 인스턴스만 비친다.
 * 방식(0 없음·1 잘라내기·2 디더)과 무관하게 적용된다: 분수·가로등·상점처럼 컷 대상이 아닌 개체가 영웅을 덮을 때 쓴다.
 */
export const OCCLUDER_SLOTS = 4;
const occU = [0, 1, 2, 3].map(() => uniform(new THREE.Vector3(1e6, 1e6, 0)));
const occCpu = new Float32Array(OCCLUDER_SLOTS * 3).fill(0);
/** 칸 i에 가림 물체 중심(x, z)과 비침 정도를 넣는다(amount 0이면 끔) */
export function setOccluderFade(i: number, x: number, z: number, amount: number): void {
  if (i < 0 || i >= OCCLUDER_SLOTS) return;
  const a = Number.isFinite(amount) ? Math.min(1, Math.max(0, amount)) : 0;
  const on = a > 0 && Number.isFinite(x) && Number.isFinite(z);
  occU[i].value.set(on ? x : 1e6, on ? z : 1e6, on ? a : 0);
  occCpu[i * 3] = on ? x : 1e6; occCpu[i * 3 + 1] = on ? z : 1e6; occCpu[i * 3 + 2] = on ? a : 0;
}
/** 중심이 (cx, cz)인 인스턴스의 가림 비침 정도(셰이더와 같은 식, 시험·QA용) */
export function occluderFadeAt(cx: number, cz: number): number {
  let m = 0;
  for (let i = 0; i < OCCLUDER_SLOTS; i++) {
    if (Math.abs(cx - occCpu[i * 3]) < 0.05 && Math.abs(cz - occCpu[i * 3 + 1]) < 0.05) m = Math.max(m, occCpu[i * 3 + 2]);
  }
  return m;
}

type N = THREE.Node<'float'>;

function cutK(cx: N, cz: N): N {
  const dx = cx.sub(cutHero.x), localZ = cz.sub(cutHero.y);
  const dz = dx.mul(cutForward.x).add(localZ.mul(cutForward.y)).negate();
  const side = abs(dx.mul(cutForward.y).negate().add(localZ.mul(cutForward.x)));
  return smoothstep(-0.3, 1.0, dz)
    .mul(float(1).sub(smoothstep(cutFar.sub(3), cutFar, dz)))
    .mul(float(1).sub(smoothstep(9, 12, side)))
    .mul(cutOn) as N;
}

/** 그레이박스용: 정점 속성 aCut(중심 x, 중심 z, 허용 0/1) */
export function cutPositionNode() {
  const a = attribute('aCut', 'vec3');
  const k = cutK(a.x as N, a.y as N).mul(a.z);
  const p = positionLocal;
  return vec3(p.x, mix(p.y, min(p.y, CUT_H), k), p.z);
}

export interface KitCutNodes {
  /** true면 그린다: 카메라 쪽이면 CUT_H 위를 잘라낸다(뒷면이 단면 뚜껑으로 보인다) */
  keep: ReturnType<N['lessThan']>;
  /** 디더로 비칠 정도 0..1 */
  dither: N;
}

/**
 * 키트 인스턴스용: 인스턴스 속성 aCut(중심 x, 중심 z, 방식 0 없음·1 잘라내기·2 디더, 인스턴스 y배율).
 * 높이를 눌러 담으면 큰 아치·무너진 벽이 늘어난 판이 되어, 대신 CUT_H 위 조각을 버리고 뒷면을 단면색으로 칠한다.
 * 디더는 영웅 바로 앞 가림체(시선 좌우 ±2.6m, 4.6m에서 0)만 비친다(2026-11-04 RG-013: 영웅을 안 가리는 나무·기둥까지 비쳐 몹 무리 위가 격자 무늬로 지저분했다).
 */
export function kitCutNodes(): KitCutNodes {
  const a = attribute('aCut', 'vec4');
  const cx = a.x as N, cz = a.y as N;
  const k = cutK(cx, cz);
  const mode = a.z;
  const clip = k.mul(float(1).sub(abs(mode.sub(1))).max(0));
  const dx = cx.sub(cutHero.x), localZ = cz.sub(cutHero.y);
  const dz = dx.mul(cutForward.x).add(localZ.mul(cutForward.y)).negate();
  const side = abs(dx.mul(cutForward.y).negate().add(localZ.mul(cutForward.x)));
  const kd = smoothstep(-0.3, 1.0, dz)
    .mul(float(1).sub(smoothstep(ditherFar.sub(3), ditherFar, dz)))
    .mul(float(1).sub(smoothstep(2.6, 4.6, side)))
    .mul(cutOn);
  // 보스 시선 통로: 보스→카메라 선분 근처의 디더 개체(현무암 기둥 등)도 비친다(보스가 아레나 가장자리로 가면 앞 기둥이 화면을 가렸다)
  const ux = cutCam.x.sub(cutBoss.x), uz = cutCam.y.sub(cutBoss.y);
  const len2 = ux.mul(ux).add(uz.mul(uz)).max(1e-4);
  const px = cx.sub(cutBoss.x), pz = cz.sub(cutBoss.y);
  const tb = px.mul(ux).add(pz.mul(uz)).div(len2);
  const perp = abs(px.mul(uz).sub(pz.mul(ux))).div(len2.sqrt());
  const kb = smoothstep(0.06, 0.2, tb)
    .mul(float(1).sub(smoothstep(1.02, 1.12, tb)))
    .mul(float(1).sub(smoothstep(cutBossLane, cutBossLane.add(3), perp)))
    .mul(cutBoss.z).mul(cutOn).mul(1.2);
  // 보스전: 카메라 바로 앞(수평 14m 안) 큰 기둥은 화면 가장자리를 통째로 덮으므로 함께 비친다
  const dc = vec2(cx.sub(cutCam.x), cz.sub(cutCam.y)).length();
  const kc = float(1).sub(smoothstep(9, 14, dc)).mul(cutBoss.z).mul(cutOn);
  // 카메라 리그가 고른 가림 물체(방식 무관): 중심이 칸과 같으면 그 정도만큼 비친다
  let ko: N = float(0) as N;
  for (const u of occU) {
    const same = float(1).sub(smoothstep(0.03, 0.05, abs(cx.sub(u.x)).max(abs(cz.sub(u.y)))));
    ko = ko.max(same.mul(u.z)) as N;
  }
  const di = kd.max(kb).max(kc).mul(mode.sub(1).max(0)).max(ko.mul(cutOn));
  // 잘라낼 높이: 컷 정도에 따라 7.5m에서 CUT_H로 내려온다(컷이 없으면 아무것도 버리지 않는다)
  const clipY = varying(mix(float(7.5), float(CUT_H), clip).add(float(1).sub(smoothstep(0.0, 0.002, clip)).mul(1000))) as N;
  return {
    keep: positionWorld.y.lessThan(clipY),
    dither: varying(di) as N,
  };
}

/** 화면 고정 디더 마스크: true면 그린다. 강도 0..1에서 최대 72%까지 비운다. */
export function ditherKeep(amount: N) {
  const c = screenCoordinate;
  const ign = fract(float(52.9829189).mul(fract(c.x.mul(0.06711056).add(c.y.mul(0.00583715)))));
  return ign.greaterThanEqual(amount.mul(0.72));
}
